import { useContext, useEffect, useRef, FC } from 'react'
import { connect } from 'react-redux'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { compose } from '@reduxjs/toolkit'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import get from 'lodash/get'

import { RootState } from 'typedRedux/configureStoreWithHistory'
import useDispatch from 'hooks/useDispatch'
import { registerUser } from 'actions/authActions'
import { Submit } from 'components/form'
import { dispatchRollbarError } from 'utils/rollbar'
import RailsContext from 'contexts/RailsContext'

import AuthTextField from './AuthTextField'
import AuthLocationField from './AuthLocationField'
import locationSchema from './locationSchema'
import withCurrentVisit from './withCurrentVisit'
import {
  AuthToken,
  Terms,
  FacebookLogin,
  GoogleLogin,
  AppleLogin,
} from './shared'
import { Divider, FieldWrapper } from './styles'

interface SignupProps {
  onSuccess?: () => any
  onConfirmEmail: () => any
  serverSide?: boolean
  referredById?: string
  referralType?: string
}

interface VisitProps {
  location: { name: string; lng: number; lat: number }
}

interface InjectedProps {
  auth: { loading: boolean; referredById?: string; referralType?: string }
  currentVisit?: VisitProps
}

interface FormValues {
  user: {
    email: string
    password: string
    password_confirmation: string
    first_name: string
    last_name: string
    location: string | null
    latitude: number | null
    longitude: number | null
    referred_by_id: string | null
    referral_type: string | null
  }
  recaptcha_token: string | null
}

const validationSchema = Yup.object({
  user: Yup.object({
    email: Yup.string()
      .required('This field is required')
      .email('Not a valid email'),
    password: Yup.string()
      .required('This field is required')
      .min(8, 'Password should be at least 8+ characters'),
    password_confirmation: Yup.string()
      .required('This field is required')
      .oneOf(
        [Yup.ref('password')],
        'Password confirmation should match password',
      ),
    first_name: Yup.string().required('This field is required'),
    last_name: Yup.string().required('This field is required'),
    ...locationSchema,
  }),
})

const Signup: FC<SignupProps & InjectedProps> = ({
  auth,
  onSuccess,
  onConfirmEmail,
  serverSide = false,
  referredById,
  referralType,
  currentVisit,
}) => {
  const dispatch = useDispatch()
  const formElement = useRef<HTMLFormElement>(null)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const effectiveReferredById = referredById ?? auth.referredById ?? null
  const effectiveReferralType = referralType ?? auth.referralType ?? null

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      user: {
        email: '',
        password: '',
        password_confirmation: '',
        first_name: '',
        last_name: '',
        location: currentVisit?.location?.name ?? null,
        latitude: currentVisit?.location?.lat ?? null,
        longitude: currentVisit?.location?.lng ?? null,
        referred_by_id: effectiveReferredById,
        referral_type: effectiveReferralType,
      },
      recaptcha_token: null,
    },
  })

  useEffect(() => {
    const { dirtyFields } = form.formState
    const current = form.getValues()
    const keep = (path: string, fresh: any) =>
      get(dirtyFields, path) ? get(current, path) : fresh
    form.reset({
      user: {
        email: keep('user.email', ''),
        password: keep('user.password', ''),
        password_confirmation: keep('user.password_confirmation', ''),
        first_name: keep('user.first_name', ''),
        last_name: keep('user.last_name', ''),
        location: keep('user.location', currentVisit?.location?.name ?? null),
        latitude: keep('user.latitude', currentVisit?.location?.lat ?? null),
        longitude: keep('user.longitude', currentVisit?.location?.lng ?? null),
        referred_by_id: keep('user.referred_by_id', effectiveReferredById),
        referral_type: keep('user.referral_type', effectiveReferralType),
      },
      recaptcha_token: keep('recaptcha_token', null),
    })
  }, [
    currentVisit?.location?.name,
    currentVisit?.location?.lat,
    currentVisit?.location?.lng,
    effectiveReferredById,
    effectiveReferralType,
    form,
  ])

  const setRecaptchaToken = (token: string) => {
    const input = formElement.current?.elements.namedItem(
      'recaptcha_token',
    ) as HTMLInputElement | null
    if (input) input.value = token
  }

  const fetchRecaptchaToken = async () => {
    if (!executeRecaptcha) return null
    try {
      return await executeRecaptcha('signup')
    } catch (error) {
      dispatchRollbarError('Recaptcha failed at signup', {
        originalError: (error as Error)?.toString(),
      })
      return null
    }
  }

  const onSubmit = async (formValues: FormValues) => {
    dispatch({ type: 'USER_REGISTER_PENDING' })
    const recaptchaToken = await fetchRecaptchaToken()
    if (serverSide) {
      setRecaptchaToken(recaptchaToken || '')
      formElement.current?.submit()
    } else {
      // @ts-ignore -- registerUser not typed, dispatch returns AsyncAction not Promise
      await dispatch(
        registerUser({ ...formValues, recaptcha_token: recaptchaToken }),
      )
      onSuccess?.()
    }
  }

  return (
    <FormProvider {...form}>
      <AppleLogin>Sign up with Apple</AppleLogin>
      <GoogleLogin>Sign up with Google</GoogleLogin>
      <FacebookLogin
        serverSide={serverSide}
        onSuccess={onSuccess}
        onConfirmEmail={onConfirmEmail}
      >
        Sign up with Facebook
      </FacebookLogin>

      <form
        className="qa-sign-up"
        action="/users"
        method="post"
        ref={formElement}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <AuthToken />

        <Divider>
          <span>or</span>
        </Divider>

        <FieldWrapper>
          <AuthTextField
            label="Email"
            name="user[email]"
            type="email"
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper>
          <AuthTextField
            label="Password"
            name="user[password]"
            type="password"
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper>
          <AuthTextField
            label="Confirm password"
            name="user[password_confirmation]"
            type="password"
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper columns>
          <AuthTextField label="First name" name="user[first_name]" noMargin />
          <AuthTextField label="Last name" name="user[last_name]" noMargin />
        </FieldWrapper>

        <FieldWrapper margin="medium">
          <AuthLocationField noMargin />
        </FieldWrapper>

        {effectiveReferredById && (
          <input
            type="hidden"
            name="user[referred_by_id]"
            value={effectiveReferredById}
          />
        )}
        {effectiveReferralType && (
          <input type="hidden" name="user[referral_type]" value="travel_crew" />
        )}
        <input type="hidden" name="recaptcha_token" defaultValue="" />

        <FieldWrapper>
          <Submit
            disabled={auth.loading}
            fullwidth
            uppercase={false}
            aria-label="Submit sign up form"
          >
            Sign up
          </Submit>
        </FieldWrapper>

        <Terms />
      </form>
    </FormProvider>
  )
}

const ConnectedSignup: FC<SignupProps> = compose(
  withCurrentVisit,
  connect(({ auth }: { auth: RootState['auth'] }) => ({
    auth,
  })),
)(Signup)

// Wrap with GoogleReCaptchaProvider here rather than at the App root
// so the ~80 KB gz of external reCAPTCHA JS only loads once a user
// actually reaches the signup flow — the only place in the app that
// calls useGoogleReCaptcha(). Both the /signup route (Signup.tsx) and
// the AuthModal's signup tab mount this component, so both pick up
// the provider transparently.
const SignupFormWithRecaptcha: FC<SignupProps> = (props) => {
  const railsContext = useContext(RailsContext)
  const reCaptchaKey = railsContext?.env?.REACT_APP_RECAPTCHA_KEY
  if (!reCaptchaKey) {
    // Without the key the provider would inject a broken script tag;
    // fall back to rendering the form bare. The submit path already
    // tolerates `executeRecaptcha` being undefined.
    return <ConnectedSignup {...props} />
  }
  return (
    <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
      <ConnectedSignup {...props} />
    </GoogleReCaptchaProvider>
  )
}

export default SignupFormWithRecaptcha
